import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  MapPinIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const OriginationDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    leads: {
      totalLeads: 1247,
      convertedLeads: 233,
      conversionRate: 18.7,
      todayLeads: 23,
      pipelineValue: 12500000
    },
    channels: [
      { name: 'Parcerias com Escritórios', leads: 623, converted: 142, rate: 22.8 },
      { name: 'Marketing Digital', leads: 374, converted: 67, rate: 17.9 },
      { name: 'Indicações', leads: 186, converted: 18, rate: 9.7 },
      { name: 'Eventos Jurídicos', leads: 64, converted: 6, rate: 9.4 }
    ],
    pipeline: [
      { stage: 'Leads Qualificados', count: 845, value: 8500000 },
      { stage: 'Apresentação', count: 234, value: 3400000 },
      { stage: 'Proposta Enviada', count: 89, value: 1800000 },
      { stage: 'Negociação', count: 45, value: 1200000 },
      { stage: 'Fechamento', count: 23, value: 650000 }
    ],
    regions: [
      { state: 'SP', leads: 345, converted: 78, conversionRate: 22.6 },
      { state: 'RJ', leads: 198, converted: 41, conversionRate: 20.7 },
      { state: 'MG', leads: 156, converted: 28, conversionRate: 17.9 },
      { state: 'RS', leads: 134, converted: 31, conversionRate: 23.1 },
      { state: 'PR', leads: 89, converted: 15, conversionRate: 16.9 }
    ]
  });
  const [loading, setLoading] = useState(false);

  // Simulação de dados em tempo real
  useEffect(() => {
    const interval = setInterval(() => {
      setDashboardData(prev => ({
        ...prev,
        leads: {
          ...prev.leads,
          todayLeads: prev.leads.todayLeads + Math.floor(Math.random() * 3)
        }
      }));
    }, 30000); // Atualiza a cada 30 segundos

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados de originação...</p>
        </div>
      </div>
    );
  }

  const { leads, channels, pipeline, regions } = dashboardData;

  return (
    <div className="space-y-4">
      <div>
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Dashboard de Originação
          </h1>
          <p className="text-gray-600">
            Gestão completa do funil de captação de créditos - ECP Capital
          </p>
        </div>

        {/* KPIs Principais de Originação */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Total de Leads</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{leads.totalLeads.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Base ativa</p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <UserGroupIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Conversões</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{leads.convertedLeads}</p>
                <p className="text-xs text-gray-500 mt-1">Leads fechados</p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Taxa de Conversão</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{leads.conversionRate}%</p>
                <p className="text-xs text-gray-500 mt-1">Meta: 18.7%</p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Leads Hoje</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{leads.todayLeads}</p>
                <p className="text-xs text-gray-500 mt-1">Novos prospects</p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Pipeline</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">R$ {(leads.pipelineValue / 1000000).toFixed(1)}M</p>
                <p className="text-xs text-gray-500 mt-1">Valor potencial</p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                <CurrencyDollarIcon className="h-5 w-5 text-teal-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Performance por Canal */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              Performance por Canal
            </h3>
            <div className="space-y-3">
              {channels.map((channel, index) => (
                <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-gray-900 font-medium">{channel.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${
                      channel.rate >= 20 ? 'bg-green-50 border-green-200 text-green-700' :
                      channel.rate >= 15 ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                      'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      {channel.rate}%
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Leads</p>
                      <p className="text-blue-600 font-semibold">{channel.leads}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Convertidos</p>
                      <p className="text-green-600 font-semibold">{channel.converted}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Taxa</p>
                      <p className="text-purple-600 font-semibold">{channel.rate}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Funil de Vendas */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-gray-500" />
              Funil de Vendas
            </h3>
            <div className="space-y-3">
              {pipeline.map((stage, index) => {
                const maxCount = Math.max(...pipeline.map(s => s.count));
                const widthPercent = (stage.count / maxCount) * 100;

                return (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 text-sm font-medium">{stage.stage}</span>
                      <div className="text-right">
                        <span className="text-gray-900 font-semibold">{stage.count}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          R$ {(stage.value / 1000).toFixed(0)}k
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${widthPercent}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Análise Geográfica de Originação */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Performance por Estado */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPinIcon className="h-5 w-5 text-gray-500" />
              Performance por Estado
            </h3>
            <div className="space-y-2">
              {regions.map((region, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-900 font-bold text-lg w-8">{region.state}</span>
                    <div>
                      <div className="text-gray-900 font-medium">{region.leads} leads</div>
                      <div className="text-xs text-gray-500">{region.converted} conversões</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                      region.conversionRate >= 22 ? 'bg-green-50 border-green-200 text-green-700' :
                      region.conversionRate >= 18 ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                      'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      {region.conversionRate}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Atividades Recentes */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-gray-500" />
              Atividades Recentes
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <PhoneIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-gray-900 text-sm">Ligação agendada com Escritório Silva & Advogados</p>
                  <p className="text-xs text-gray-500 mt-1">Há 15 minutos • Lead qualificado SP</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <EnvelopeIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-gray-900 text-sm">Proposta ECF Balance enviada</p>
                  <p className="text-xs text-gray-500 mt-1">Há 32 minutos • R$ 2.5M em precatórios</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <DocumentTextIcon className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-gray-900 text-sm">Contrato assinado - ECF Premium</p>
                  <p className="text-xs text-gray-500 mt-1">Há 1 hora • Advogado João Mendes - RJ</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <ArrowTrendingUpIcon className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-gray-900 text-sm">Meta mensal atingida</p>
                  <p className="text-xs text-gray-500 mt-1">Há 2 horas • 18.7% de conversão alcançada</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Metas e Objetivos */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-4">
          <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
            <ArrowTrendingUpIcon className="h-5 w-5 text-gray-500" />
            Metas de Originação
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Meta de Conversão */}
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-gray-900 font-medium">Taxa de Conversão</h4>
                <span className="text-green-600 text-sm font-semibold">{leads.conversionRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-green-500 h-3 rounded-full"
                  style={{ width: `${(leads.conversionRate / 18.7) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">Meta: 18.7% • Progresso: {Math.round((leads.conversionRate / 18.7) * 100)}%</p>
            </div>

            {/* Meta de Pipeline */}
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-gray-900 font-medium">Pipeline Value</h4>
                <span className="text-blue-600 text-sm font-semibold">R$ {(leads.pipelineValue / 1000000).toFixed(1)}M</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-blue-500 h-3 rounded-full"
                  style={{ width: `${(leads.pipelineValue / 15000000) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">Meta: R$ 15M • Progresso: {Math.round((leads.pipelineValue / 15000000) * 100)}%</p>
            </div>

            {/* Meta de Leads */}
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-gray-900 font-medium">Leads Mensais</h4>
                <span className="text-purple-600 text-sm font-semibold">{leads.totalLeads}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-purple-500 h-3 rounded-full"
                  style={{ width: `${(leads.totalLeads / 1500) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">Meta: 1.500 • Progresso: {Math.round((leads.totalLeads / 1500) * 100)}%</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm">
          <p>Dashboard de Originação ECP Capital • Focado em captação e conversão de leads • Atualizado em tempo real</p>
        </div>
      </div>
    </div>
  );
};

export default OriginationDashboard;